import React from "react";
import PropTypes from "prop-types";
import { Typography, Box } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { Badge, Btn } from "./UI";
import { ServiceForm, ResourceForm } from "./Forms";
import ServiceDetailInfo from "./ServiceDetailInfo";
import ResourceDetailInfo from "./ResourceDetailInfo";
import ViewAtLink from "./ViewAtLink";
import { useStyles } from "./config-card.style";

function getCardStatusInfo(isService, item) {
  const isActive = isService ? item.available : item.active;
  if (isActive) return { statusType: "active", statusLabel: "Active" };
  if (isService) return { statusType: "suspended", statusLabel: "Suspended" };
  return { statusType: "inactive", statusLabel: "Inactive" };
}

export const ConfigCard = ({
  item,
  type,
  isEditing,
  editData,
  deleteConfirmation,
  onStartEdit,
  onCancelEdit,
  onEditDataChange,
  onSaveEdit,
  onDeleteClick,
  onDeleteConfirm,
  onClearDeleteConfirm,
  onGoToInheritedLocation,
  servicesRef,
  resourcesRef,
}) => {
  const classes = useStyles();
  const isService = type === "svc";
  const isDirect = item.origin === "direct";
  const { statusType, statusLabel } = getCardStatusInfo(isService, item);
  const showDeleteConfirm = deleteConfirmation?.type === type && deleteConfirmation?.id === item.id;

  const handleCardClick = () => {
    if (!isDirect) return null;
    return isEditing ? onCancelEdit() : onStartEdit(item, type);
  };

  const renderBadges = () => (
    <Box className={classes.badgeRow}>
      <Typography component="span" className={classes.itemName}>{item.name}</Typography>
      {isDirect && <Badge type="direct">Direct</Badge>}
      {!isDirect && <Badge type="inherited">{`Inherited · ${item.fromName}`}</Badge>}
      <Badge type={statusType}>{statusLabel}</Badge>
      {!isService && item.autoAssign && <Badge type="tag">Auto-Assign</Badge>}
    </Box>
  );

  const renderDetails = () => (
    <Box className={classes.detailRow}>
      {isService && <ServiceDetailInfo item={item} />}
      {!isService && <ResourceDetailInfo item={item} />}
    </Box>
  );

  const renderEditForm = () => (
    <Box className={classes.editFormArea}>
      {isService && (
        <ServiceForm
          data={editData}
          onChange={onEditDataChange}
          servicesRef={servicesRef}
          displayNameWhenNoRefs={item.name}
          hideIdentitySelector
        />
      )}
      {!isService && (
        <ResourceForm
          data={editData}
          onChange={onEditDataChange}
          resourcesRef={resourcesRef}
          displayNameWhenNoRefs={item.name}
          hideIdentitySelector
        />
      )}
      <Box className={classes.editFormActions}>
        <Btn variant="outlined" size="small" onClick={onCancelEdit}>
          Cancel
        </Btn>
        <Btn size="small" onClick={onSaveEdit}>
          Save Changes
        </Btn>
      </Box>
    </Box>
  );

  const renderDeleteConfirm = () => (
    <Box className={classes.deleteConfirmArea}>
      <Typography className={classes.deleteConfirmText}>
        Remove <strong>{item.name}</strong>?
      </Typography>
      <Box className={classes.deleteConfirmActions}>
        <Btn
          variant="outlined"
          size="small"
          onClick={onClearDeleteConfirm}
          sx={{ borderColor: "#c62828", color: "#c62828" }}
        >
          Cancel
        </Btn>
        <Btn size="small" onClick={() => onDeleteConfirm(item.id, type)} sx={{ background: "#c62828" }}>
          Confirm
        </Btn>
      </Box>
    </Box>
  );

  return (
    <Box className={classes.card}>
      {isDirect && (
        <button
          type="button"
          onClick={handleCardClick}
          className={classes.cardButton}
        >
          <Box className={classes.cardContent}>
            {renderBadges()}
            {renderDetails()}
          </Box>
          <Box className={classes.actionsGroup}>
            <Typography component="span" className={classes.expandCollapseLabel}>
              {isEditing ? "Collapse" : "Expand"}
              {isEditing ? (
                <ExpandLessIcon className={classes.expandCollapseIcon} aria-hidden />
              ) : (
                <ExpandMoreIcon className={classes.expandCollapseIcon} aria-hidden />
              )}
            </Typography>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onDeleteClick(item.id, type);
              }}
              className={classes.deleteButton}
              aria-label={`Remove ${item.name}`}
            >
              ✕
            </button>
          </Box>
        </button>
      )}
      {!isDirect && (
        <Box className={classes.inheritedRow}>
          <Box className={classes.cardContent}>
            {renderBadges()}
            {renderDetails()}
          </Box>
          <ViewAtLink onClick={() => onGoToInheritedLocation(item.fromLoc)} fromName={item.fromName} />
        </Box>
      )}
      {isEditing && editData && renderEditForm()}
      {showDeleteConfirm && renderDeleteConfirm()}
    </Box>
  );
};

const itemShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  origin: PropTypes.string,
  fromName: PropTypes.string,
  fromLoc: PropTypes.string,
  available: PropTypes.bool,
  active: PropTypes.bool,
  availType: PropTypes.string,
  sso: PropTypes.bool,
  autoProv: PropTypes.bool,
  externalId: PropTypes.string,
  explorable: PropTypes.bool,
  autoAssign: PropTypes.bool,
  program: PropTypes.string,
  hidden: PropTypes.bool,
});

ConfigCard.propTypes = {
  item: itemShape.isRequired,
  type: PropTypes.string.isRequired,
  isEditing: PropTypes.bool,
  editData: PropTypes.object,
  deleteConfirmation: PropTypes.shape({
    type: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onStartEdit: PropTypes.func,
  onCancelEdit: PropTypes.func,
  onEditDataChange: PropTypes.func,
  onSaveEdit: PropTypes.func,
  onDeleteClick: PropTypes.func,
  onDeleteConfirm: PropTypes.func,
  onClearDeleteConfirm: PropTypes.func,
  onGoToInheritedLocation: PropTypes.func,
  servicesRef: PropTypes.array,
  resourcesRef: PropTypes.array,
};

ConfigCard.defaultProps = {
  isEditing: false,
  editData: null,
  deleteConfirmation: null,
  onStartEdit: undefined,
  onCancelEdit: undefined,
  onEditDataChange: undefined,
  onSaveEdit: undefined,
  onDeleteClick: undefined,
  onDeleteConfirm: undefined,
  onClearDeleteConfirm: undefined,
  onGoToInheritedLocation: undefined,
  servicesRef: [],
  resourcesRef: [],
};
